import { useState, useEffect } from 'react'
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, IconButton,
  CircularProgress, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Alert, Select, MenuItem, FormControl,
  InputLabel, Tabs, Tab,
} from '@mui/material'
import { Add, Delete, ContentCopy, Refresh, Download } from '@mui/icons-material'
import { useSelector } from 'react-redux'
import { tenantApi, scannerApi } from '../services/api'
import { RootState } from '../store'

const ScannersPage = () => {
  const [scanners, setScanners] = useState<any[]>([])
  const [submissions, setSubmissions] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [registrationResult, setRegistrationResult] = useState<any>(null)
  const [tab, setTab] = useState(0)
  const { user } = useSelector((state: RootState) => state.auth)
  const isAdmin = user?.type === 'admin'
  // RBAC-5 — viewers can read scanner state but can't provision / delete /
  // rotate. Backend already enforces (require_role(Role.ANALYST/MANAGER)
  // on the write endpoints) as of 2026-06-04; the frontend gate is
  // defense-in-depth so viewers don't see buttons that will 403.
  const canWrite = user?.role !== 'viewer'
  const [formData, setFormData] = useState({
    name: '',
    tenant_id: user?.tenant_id || '',
    description: '',
    deployment_type: 'docker',
  })

  useEffect(() => { fetchData() }, [])

  const fetchData = async () => {
    try {
      if (isAdmin) {
        // Platform admin: one cross-tenant inventory call.
        //
        // This used to loop every tenant calling getTenantScanners(t.id). That
        // route is now tenant-owned and refuses platform admins — and because
        // each call was wrapped in `catch {}`, the page would have shown an
        // empty list rather than an error. The inventory endpoint answers the
        // operator's question directly and returns no credentials, which the
        // per-tenant route did.
        const [submissionsRes, inventoryRes] = await Promise.all([
          scannerApi.getAdminSubmissions().catch(() => ({ data: [] })),
          tenantApi.getScannerInventory({ limit: 500 }),
        ])

        const list = inventoryRes.data.scanners || []
        setScanners(list.filter((s: any) => s.status !== 'revoked'))
        setSubmissions(submissionsRes.data.submissions || submissionsRes.data || [])
      } else {
        // Tenant user: fetch only their tenant's scanners
        const tenantId = user?.tenant_id
        if (tenantId) {
          const res = await tenantApi.getTenantScanners(tenantId)
          const list = res.data.scanners || res.data || []
          setScanners(list.filter((s: any) => s.status !== 'revoked'))
          setFormData(prev => ({ ...prev, tenant_id: tenantId }))
        }
      }
    } catch (e) {
      console.error('Failed to fetch data:', e)
    } finally {
      setLoading(false)
    }
  }

  const handleRegister = async () => {
    if (!formData.tenant_id) {
      // The tenant selector is gone — provisioning is scoped to the caller's
      // own tenant, taken from the session. Reaching this means the session
      // carries no tenant, which the user cannot fix by choosing one.
      alert('Your session is not scoped to a tenant, so a scanner cannot be provisioned. Sign in again, or contact your administrator.')
      return
    }
    try {
      const res = await tenantApi.createScanner(formData.tenant_id, {
        name: formData.name,
        description: formData.description,
        deployment_type: formData.deployment_type,
      })
      setRegistrationResult(res.data)
      fetchData()
    } catch (e: any) {
      const detail = e.response?.data?.detail || 'Failed to create scanner'
      alert(detail)
    }
  }

  const handleDelete = async (scannerId: string) => {
    if (!confirm('Delete this scanner? This cannot be undone.')) return
    try {
      await tenantApi.deleteScanner(scannerId)
      fetchData()
    } catch (e) {
      console.error('Failed to delete scanner:', e)
    }
  }

  const handleDownloadPackage = async (scannerId: string) => {
    try {
      const res = await tenantApi.getDeploymentPackage(scannerId)
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const a = document.createElement('a')
      a.href = url
      a.download = `scanner-${scannerId.substring(0, 8)}-deploy.zip`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (e) {
      console.error('Failed to download deployment package:', e)
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  const statusColor = (status: string) => {
    const map: any = { activated: 'success', provisioned: 'warning', active: 'success', registered: 'info', connected: 'success', inactive: 'default', revoked: 'error', error: 'error' }
    return map[status] || 'default'
  }

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Scanners</Typography>
        <Box>
          <Button variant="outlined" startIcon={<Refresh />} onClick={fetchData} sx={{ mr: 1 }}>
            Refresh
          </Button>
          {/* Platform admins no longer provision: a scanner API key
              authenticates as the tenant, so issuing one is the tenant's act.
              canWrite is role-only (role !== 'viewer') and cannot express
              that, so the identity check is explicit. */}
          {canWrite && !isAdmin && (
            <Button variant="contained" startIcon={<Add />} onClick={() => { setDialogOpen(true); setRegistrationResult(null) }}>
              Provision Scanner
            </Button>
          )}
        </Box>
      </Box>

      {isAdmin && (
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label={`Scanners (${scanners.length})`} />
          <Tab label={`Submissions (${submissions.length})`} />
        </Tabs>
      )}

      {/* Scanners Tab */}
      {tab === 0 && (
        <Card>
          <CardContent>
            {scanners.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No scanners provisioned. Click "Provision Scanner" to create one.
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      {/* Only for platform admins: their list spans tenants and
                          is unreadable without knowing whose scanner each row
                          is. A tenant user's list is single-tenant by
                          definition, where the column would repeat one value. */}
                      {isAdmin && <TableCell>Tenant</TableCell>}
                      <TableCell>Scanner ID</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Last Heartbeat</TableCell>
                      <TableCell>Scans</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {scanners.map((s: any) => (
                      <TableRow key={s.scanner_id || s.id} hover>
                        <TableCell>{s.name || s.scanner_name}</TableCell>
                        {isAdmin && (
                          <TableCell>{s.tenant_name || s.tenant_id || '—'}</TableCell>
                        )}
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {(s.scanner_id || s.id)?.substring(0, 12)}...
                        </TableCell>
                        <TableCell>
                          <Chip label={s.status || 'provisioned'} color={statusColor(s.status || 'provisioned') as any} size="small" />
                        </TableCell>
                        <TableCell>{s.deployment_type || 'docker'}</TableCell>
                        <TableCell>{s.last_heartbeat ? new Date(s.last_heartbeat).toLocaleString() : 'Never'}</TableCell>
                        <TableCell>{s.total_scans || s.submission_count || 0}</TableCell>
                        <TableCell>
                          <IconButton size="small" title="Copy Scanner ID" onClick={() => copyToClipboard(s.scanner_id || s.id)}>
                            <ContentCopy fontSize="small" />
                          </IconButton>
                          {/* Same gate as the backend: ANALYST and above, own
                              tenant only. A viewer and a platform admin both
                              get 403, so neither should see the button. */}
                          {canWrite && !isAdmin && (
                            <IconButton size="small" title="Download Deployment Package" onClick={() => handleDownloadPackage(s.scanner_id || s.id)}>
                              <Download fontSize="small" />
                            </IconButton>
                          )}
                          {canWrite && !isAdmin && (
                            <IconButton size="small" title="Delete Scanner" onClick={() => handleDelete(s.scanner_id || s.id)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Submissions Tab */}
      {tab === 1 && (
        <Card>
          <CardContent>
            {submissions.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No submissions yet</Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Scanner</TableCell>
                      <TableCell>Findings</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Submitted</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {submissions.map((sub: any, i: number) => (
                      <TableRow key={i} hover>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {sub.scanner_id?.substring(0, 12)}
                        </TableCell>
                        <TableCell>{sub.findings_count || 0}</TableCell>
                        <TableCell>
                          <Chip label={sub.status || 'processed'} color="success" size="small" />
                        </TableCell>
                        <TableCell>{new Date(sub.timestamp || sub.created_at || sub.submitted_at).toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Provision Scanner Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{registrationResult ? 'Scanner Provisioned' : 'Provision New Scanner'}</DialogTitle>
        <DialogContent>
          {registrationResult ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>Scanner provisioned successfully!</Alert>

              <Alert severity="warning" sx={{ mb: 2 }}>
                Save these credentials now — they will not be shown again.
              </Alert>

              <Typography variant="subtitle2" gutterBottom>Scanner ID:</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', flexGrow: 1 }}>
                  {registrationResult.id}
                </Typography>
                <IconButton size="small" onClick={() => copyToClipboard(registrationResult.id)}>
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Box>

              {registrationResult.setup_token && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Setup token (one-time use, expires 24h): <strong>{registrationResult.setup_token}</strong>
                  <IconButton size="small" onClick={() => copyToClipboard(registrationResult.setup_token)} sx={{ ml: 1 }}>
                    <ContentCopy sx={{ fontSize: 14 }} />
                  </IconButton>
                </Alert>
              )}

              <Typography variant="h6" sx={{ mt: 1, mb: 1 }}>
                Quick Start — {formData.deployment_type === 'cli' ? 'CLI' :
                  formData.deployment_type === 'ci-cd' ? 'GitHub / GitLab' :
                  formData.deployment_type === 'aws' ? 'AWS CodeBuild' :
                  formData.deployment_type === 'azure' ? 'Azure Pipelines' :
                  formData.deployment_type === 'gcp' ? 'Cloud Build' :
                  formData.deployment_type === 'bitbucket' ? 'Bitbucket Pipelines' : 'CI/CD'}
              </Typography>

              {/* CLI instructions */}
              {formData.deployment_type === 'cli' && (<Box>
                <Typography variant="subtitle2" gutterBottom>Step 1 — Install:</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 1, bgcolor: 'grey.900', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'grey.100', flexGrow: 1, fontSize: '0.85rem' }}>
                    {registrationResult.install_command}
                  </Typography>
                  <IconButton size="small" sx={{ color: 'grey.400' }} onClick={() => copyToClipboard(registrationResult.install_command)}>
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Box>
                <Typography variant="subtitle2" gutterBottom>Step 2 — Scan:</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, p: 1, bgcolor: 'grey.900', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'grey.100', flexGrow: 1, fontSize: '0.85rem', wordBreak: 'break-all' }}>
                    {registrationResult.run_command}
                  </Typography>
                  <IconButton size="small" sx={{ color: 'grey.400' }} onClick={() => copyToClipboard(registrationResult.run_command)}>
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Box>
              </Box>)}

              {/* Removed Docker and Kubernetes deployment types */}

              {/* CI/CD instructions */}
              {['ci-cd', 'aws', 'azure', 'gcp', 'bitbucket'].includes(formData.deployment_type) && (<Box>
                <Typography variant="subtitle2" gutterBottom>Step 1 — Download pipeline config:</Typography>
                <Button variant="outlined" startIcon={<Download />} sx={{ mb: 2 }}
                  onClick={() => handleDownloadPackage(registrationResult.id)}>
                  Download CI/CD Package
                </Button>
                <Typography variant="subtitle2" gutterBottom>Step 2 — Add to your repo:</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, p: 1, bgcolor: 'grey.900', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'grey.100', flexGrow: 1, fontSize: '0.85rem', wordBreak: 'break-all' }}>
                    unzip scanner-*.zip && bash install.sh /path/to/repo
                  </Typography>
                  <IconButton size="small" sx={{ color: 'grey.400' }} onClick={() => copyToClipboard('unzip scanner-*.zip && bash install.sh /path/to/repo')}>
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {formData.deployment_type === 'ci-cd' && 'Includes GitHub Actions and GitLab CI workflow files.'}
                  {formData.deployment_type === 'aws' && 'Includes AWS CodeBuild buildspec.yml.'}
                  {formData.deployment_type === 'azure' && 'Includes Azure Pipelines azure-pipelines.yml.'}
                  {formData.deployment_type === 'gcp' && 'Includes Google Cloud Build cloudbuild.yaml.'}
                  {formData.deployment_type === 'bitbucket' && 'Includes Bitbucket Pipelines bitbucket-pipelines.yml.'}
                  {' '}Commit the .arias/ directory and pipeline config to your repo.
                </Typography>
              </Box>)}

              <Typography variant="subtitle2" sx={{ mt: 3 }} gutterBottom>API Credentials (for reference):</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Used internally for HMAC-signed communication. The setup token handles activation automatically.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 70 }}>API Key:</Typography>
                <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {registrationResult.api_key}
                </Typography>
                <IconButton size="small" onClick={() => copyToClipboard(registrationResult.api_key)}>
                  <ContentCopy sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 70 }}>Secret:</Typography>
                <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {registrationResult.secret_key}
                </Typography>
                <IconButton size="small" onClick={() => copyToClipboard(registrationResult.secret_key)}>
                  <ContentCopy sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            </Box>
          ) : (
            <>
              {/* The tenant selector is gone. Provisioning is tenant-owned:
                  a scanner API key authenticates as the tenant, so a platform
                  admin choosing a target tenant is exactly the act that was
                  removed. A tenant user provisions only for their own tenant,
                  which formData.tenant_id already carries from the JWT. */}
              <TextField
                fullWidth label="Scanner Name" margin="normal" required
                placeholder="e.g., prod-scanner-1"
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
              />
              <TextField
                fullWidth label="Description" margin="normal" multiline rows={2}
                placeholder="e.g., Production scanner for main repository"
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>Deployment Type</InputLabel>
                <Select
                  value={formData.deployment_type}
                  label="Deployment Type"
                  onChange={e => setFormData({ ...formData, deployment_type: e.target.value })}
                >
                  <MenuItem value="cli">CLI — install and run from command line</MenuItem>
                  <MenuItem value="ci-cd">GitHub Actions / GitLab CI</MenuItem>
                  <MenuItem value="aws">AWS CodeBuild / CodePipeline</MenuItem>
                  <MenuItem value="azure">Azure DevOps Pipelines</MenuItem>
                  <MenuItem value="gcp">Google Cloud Build</MenuItem>
                  <MenuItem value="bitbucket">Bitbucket Pipelines</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                This determines the deployment package you can download after provisioning.
                All types use the same setup token for initial activation.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            {registrationResult ? 'Close' : 'Cancel'}
          </Button>
          {!registrationResult && (
            <Button variant="contained" onClick={handleRegister} disabled={!formData.name || !formData.tenant_id}>
              Provision
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ScannersPage
